import { useState, useRef } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DSModal from "../../../system-design/DSModal";
import KlydoPreview from "../../../../components/klydoPreview/KlydoPreview";
import ReviewFormModal, {
  ReviewVerdictState,
} from "../../../../components/forms/ReviewFormModal";
import SendKlydoModal from "../../components/SendKlydoModal";
import AudioPlayer from "../../../../components/AudioPlayer/AudioPlayer";
import {
  Klydo,
  PublicationReviewStates,
  hasSound,
} from "../../../../api/klydo";
import useRejectPublicationMutation from "../hooks/useRejectPublicationMutation";
import useUpdatDrafteKlydoMutation from "../hooks/useUpdateDraftKlydoMutation";
import SimpleSwitch from "../../../../components/Widgets/SimpleSwitch";
import DSGrid from "../../../system-design/DSGrid";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import DSBox from "../../../system-design/DSBox";
import DSPaper from "../../../system-design/DSPaper";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSButton from "../../../system-design/DSButton";
import DSIconButton from "../../../system-design/DSIconButton";
import { KLYDO_BLACK } from "../../../../consts/colors";
import useApprovePublicationMutation from "../hooks/useApprovePublicationMutation";
import DSErrorButton from "../../../system-design/DSErrorButton";
import { useQueryClient } from "@tanstack/react-query";
import { formatSecondsToMMSS } from "../../../../utils/formatTime";
import { getChimeDisplayName } from "../../../../utils/chimeUtils";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { download } from "../../../../utils/util";
import ReviewSocialLinks from "../../../../components/Widgets/ReviewSocialLinks";

export type KlydoReviewModalProps<T> = {
  open: boolean;
  onClose: () => void;
  row: T;
};

const KlydoReviewModal = <T,>({
  open,
  onClose,
  row,
}: KlydoReviewModalProps<T>): JSX.Element => {
  // May already be a sealed klydo: sealing (its collection was approved) and
  // having an open publication request are independent, so the queue returns both.
  const klydo = row as Klydo;
  const queryClient = useQueryClient();
  const [sendKlydoOpen, setSendKlydoOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement | null }>({});
  const [playingChime, setPlayingChime] = useState<number | null>(null);
  const [reviewState, setReviewState] = useState<PublicationReviewStates>(
    PublicationReviewStates.Pending,
  );
  const { mutate: approvePublication, isPending: isApprovePending } =
    useApprovePublicationMutation();
  const { mutate: rejectPublication, isPending: isRejectPending } =
    useRejectPublicationMutation();
  const { mutate: updateKlydoMutation } = useUpdatDrafteKlydoMutation();

  // Artist rating campaign. `row` is a static prop (not refetched), so keep the
  // subdoc in local state to reflect toggles/dates immediately. The whole
  // artistsRating subdoc is replaced on each change (top-level set), so always
  // send the full object — same behaviour as the approved klydo modal.
  const [artistsRating, setArtistsRating] = useState(klydo.artistsRating);

  const saveArtistsRating = (next: {
    active: boolean;
    startedAt?: string;
    endDate?: string;
  }) => {
    setArtistsRating(next);
    updateKlydoMutation({
      klydoId: klydo.id,
      klydo: { artistsRating: next },
    });
  };

  // When activating with no dates set, default to now → one month from now.
  const defaultRatingDates = (current?: {
    startedAt?: string;
    endDate?: string;
  }) => {
    const now = new Date();
    const inOneMonth = new Date(now);
    inOneMonth.setMonth(inOneMonth.getMonth() + 1);
    return {
      startedAt: current?.startedAt ?? now.toISOString(),
      endDate: current?.endDate ?? inOneMonth.toISOString(),
    };
  };

  const toggleRatingKlydo = (status: boolean) => {
    saveArtistsRating({
      active: status,
      startedAt: artistsRating?.startedAt,
      endDate: artistsRating?.endDate,
      ...(status && defaultRatingDates(artistsRating)),
    });
  };

  const handleRatingDateChange = (
    field: "startedAt" | "endDate",
    value: string,
  ) => {
    saveArtistsRating({
      active: artistsRating?.active ?? false,
      startedAt: artistsRating?.startedAt,
      endDate: artistsRating?.endDate,
      [field]: value ? new Date(value).toISOString() : undefined,
    });
  };

  // ISO string -> value for <input type="datetime-local"> (local time, no tz).
  const toDateTimeLocalValue = (iso?: string) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const onSubmit = () => {
    queryClient.invalidateQueries({ queryKey: ["klydos"] });
    onClose();
  };
  const isAlreadyApproved =
    klydo?.public?.reviewStatus?.state === PublicationReviewStates.Approved;

  const handleModalOpen = (state: PublicationReviewStates) => {
    setReviewState(state);
    setModalOpen(true);
  };

  const handleModalClose = () => setModalOpen(false);

  const handleSubmitReview = (message: string, state: ReviewVerdictState) => {
    if (state === PublicationReviewStates.Approved) {
      approvePublication(
        { klydoId: klydo.id, message },
        {
          onSuccess: () => {
            onSubmit();
          },
        },
      );
    } else {
      // The dedicated reject endpoint, not the generic draft update. It owns the
      // whole review record, so the client sends only the message — the state and
      // its updatedAt are the server's to set.
      rejectPublication(
        { klydoId: klydo.id, message },
        {
          onSuccess: () => {
            onSubmit();
          },
        },
      );
    }
  };

  return (
    <DSModal
      open={open}
      onClose={onClose}
      aria-labelledby="klydo-modal-title"
      aria-describedby="klydo-modal-description"
    >
      <DSBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          overflowY: "auto",
          maxHeight: "90%",
          p: 4,
        }}
      >
        <DSTypography id="klydo-modal-title" variant="h4" gutterBottom>
          Publication Request Details
        </DSTypography>
        <DSGrid container>
          <DSGrid item xs={12} md={6}>
            <DSBox
              sx={{
                width: "345px",
                height: "540px",
                position: "relative",
                boxSizing: "border-box",
                border: "1px solid white",
                backgroundColor: KLYDO_BLACK,
                borderRadius: "12px",
              }}
            >
              <KlydoPreview
                klydo={klydo}
                isMuted={isMuted}
                hasSound={hasSound(klydo?.capabilitiesV2?.animationSound?.mode)}
              />
              {hasSound(klydo?.capabilitiesV2?.animationSound?.mode) && (
                <DSIconButton
                  onClick={() => setIsMuted(!isMuted)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                    },
                  }}
                >
                  {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </DSIconButton>
              )}
              <DSBox
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  color: "white",
                  zIndex: 5,
                  fontWeight: "bold",
                }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    download(klydo.loopUrl, klydo.id);
                  }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                  <span style={{ marginLeft: "8px" }}>
                    {klydo.loopUrl.substring(
                      klydo.loopUrl.lastIndexOf(".") + 1,
                    )}
                  </span>
                </DSBox>
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const gifUrl = klydo.loopUrl.replace(/\.mp4$/, ".gif");
                    download(gifUrl, `${klydo.id}.gif`);
                  }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                  <span style={{ marginLeft: "8px" }}>gif</span>
                </DSBox>
              </DSBox>
            </DSBox>
          </DSGrid>

          <DSGrid item xs={12} md={6}>
            <DSGrid container spacing={2}>
              <DSGrid item xs={12}>
                <DSPaper variant="outlined" sx={{ p: 2 }}>
                  <DSTypography variant="h6">Basic Information</DSTypography>
                  <DSTypography>Name: {klydo?.name}</DSTypography>
                  <DSTypography>
                    Subtitle: {klydo?.subtitle || "None"}
                  </DSTypography>
                  <DSTypography>Creator ID: {klydo?.creator}</DSTypography>
                  <DSTypography>
                    Creator Name: {klydo?.author?.name}
                  </DSTypography>
                  <DSTypography>
                    Created At: {klydo?.createdAt?.toString()}
                  </DSTypography>
                  <DSTypography>
                    Updated At: {klydo?.updatedAt?.toString()}
                  </DSTypography>
                  <DSTypography>
                    {`Generated With AI: ${klydo?.metadata?.generatedByAI ? "Yes" : "No"}`}
                  </DSTypography>
                  <DSBox sx={{ mt: 1 }}>
                    <ReviewSocialLinks links={klydo?.author?.socialMedia} />
                  </DSBox>
                </DSPaper>
              </DSGrid>

              <DSGrid item xs={12}>
                <DSPaper variant="outlined" sx={{ p: 2 }}>
                  <DSTypography variant="h6">Theme</DSTypography>
                  <DSTypography>
                    Background Color: {klydo?.theme?.backgroundColor}
                  </DSTypography>
                  <DSTypography>
                    Hands Color: {klydo?.theme?.handsColor}
                  </DSTypography>
                  <DSTypography>
                    Pendulum Color: {klydo?.theme?.pendulumColor}
                  </DSTypography>
                  <DSTypography>
                    Pendulum Rod Color: {klydo?.theme?.pendulumRodColor}
                  </DSTypography>
                  {klydo?.theme?.dialsColor && (
                    <DSTypography>
                      Dials Color: {klydo?.theme?.dialsColor}
                    </DSTypography>
                  )}
                </DSPaper>
              </DSGrid>

              <DSGrid item xs={12}>
                <DSPaper variant="outlined" sx={{ p: 2 }}>
                  <DSTypography variant="h6">Media Information</DSTypography>
                  <DSTypography>
                    Loop Duration:{" "}
                    {klydo?.metadata?.klydoDurationInSeconds
                      ? formatSecondsToMMSS(
                          klydo.metadata.klydoDurationInSeconds,
                        )
                      : "N/A"}
                  </DSTypography>
                  {klydo?.metadata?.chimesDurationInSeconds && (
                    <DSTypography>
                      Chimes Duration:{" "}
                      {formatSecondsToMMSS(
                        klydo.metadata.chimesDurationInSeconds,
                      )}
                    </DSTypography>
                  )}
                  <DSTypography>
                    Sound Capability:{" "}
                    {hasSound(klydo?.capabilitiesV2?.animationSound?.mode)
                      ? "Yes"
                      : "No"}
                  </DSTypography>
                </DSPaper>
              </DSGrid>

              <DSGrid item xs={12}>
                <DSPaper variant="outlined" sx={{ p: 2 }}>
                  <DSTypography variant="h6" gutterBottom>
                    Artist Rating
                  </DSTypography>
                  <DSBox sx={{ mb: 1.5 }}>
                    <SimpleSwitch
                      title="Rating Klydo"
                      on={["", ""]}
                      default={!!artistsRating?.active}
                      onClick={toggleRatingKlydo}
                    />
                  </DSBox>
                  {artistsRating?.active && (
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <DSTextField
                        size="small"
                        label="Start date"
                        type="datetime-local"
                        value={toDateTimeLocalValue(artistsRating?.startedAt)}
                        onChange={(e) =>
                          handleRatingDateChange("startedAt", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 240 }}
                      />
                      <DSTextField
                        size="small"
                        label="End date"
                        type="datetime-local"
                        value={toDateTimeLocalValue(artistsRating?.endDate)}
                        onChange={(e) =>
                          handleRatingDateChange("endDate", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 240 }}
                      />
                    </DSBox>
                  )}
                </DSPaper>
              </DSGrid>

              {klydo?.chimesUrls && klydo.chimesUrls.length > 0 && (
                <DSGrid item xs={12}>
                  <DSPaper variant="outlined" sx={{ p: 2 }}>
                    <DSTypography variant="h6" gutterBottom>
                      Chimes
                    </DSTypography>
                    <DSBox
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {klydo.chimesUrls.map((url, index) => (
                        <AudioPlayer
                          key={index}
                          src={url}
                          title={getChimeDisplayName(
                            url,
                            index,
                            klydo?.metadata?.chimesFileName,
                          )}
                          duration={klydo?.metadata?.chimesDurationInSeconds}
                          onPlay={() => {
                            Object.entries(audioRefs.current).forEach(
                              ([key, audio]) => {
                                if (audio && parseInt(key) !== index) {
                                  audio.pause();
                                }
                              },
                            );
                            setPlayingChime(index);
                          }}
                          onPause={() => {
                            if (playingChime === index) {
                              setPlayingChime(null);
                            }
                          }}
                        />
                      ))}
                    </DSBox>
                  </DSPaper>
                </DSGrid>
              )}

              {klydo?.strikesUrls && klydo.strikesUrls.length > 0 && (
                <DSGrid item xs={12}>
                  <DSPaper variant="outlined" sx={{ p: 2 }}>
                    <DSTypography variant="h6" gutterBottom>
                      Strikes
                    </DSTypography>
                    <DSBox
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {klydo.strikesUrls.map((url, index) => (
                        <AudioPlayer
                          key={index}
                          src={url}
                          title={`Strike ${index + 1}`}
                        />
                      ))}
                    </DSBox>
                  </DSPaper>
                </DSGrid>
              )}

              <DSGrid item xs={12}>
                <DSPaper variant="outlined" sx={{ p: 2 }}>
                  <DSTypography variant="h6">Review Status</DSTypography>
                  {/* A label with nothing after it says nothing, so an empty
                      state, note or reply drops its whole line. A withdrawn
                      klydo keeps its record and loses only the state. */}
                  {klydo?.public?.reviewStatus?.state && (
                    <DSTypography>
                      State: {klydo.public.reviewStatus.state}
                    </DSTypography>
                  )}
                  {klydo?.public?.reviewStatus?.message && (
                    <DSTypography>
                      Message: {klydo.public.reviewStatus.message}
                    </DSTypography>
                  )}
                  {/* The artist's reply to that message, sent with the
                      resubmission this request is. */}
                  {klydo?.public?.reviewStatus?.comment && (
                    <DSTypography>
                      Artist Comment: {klydo.public.reviewStatus.comment}
                    </DSTypography>
                  )}
                </DSPaper>
              </DSGrid>
            </DSGrid>
          </DSGrid>
        </DSGrid>

        <DSBox
          sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}
        >
          <DSButton onClick={() => setSendKlydoOpen(true)}>
            Send to Device
          </DSButton>
          {/* Hidden, not disabled, on an already-approved request. Approval is
              terminal: the reject endpoint refuses it, because the approval claim
              is keyed on the state not being `approved` and moving it back would
              let KlydoAssetPromotionPipeline run twice. So the action does not
              exist here rather than existing and failing. */}
          {!isAlreadyApproved && (
            <DSErrorButton
              onClick={() => handleModalOpen(PublicationReviewStates.Rejected)}
            >
              Reject
            </DSErrorButton>
          )}
          <DSPrimaryButton
            disabled={isApprovePending}
            onClick={() => handleModalOpen(PublicationReviewStates.Approved)}
          >
            Approve
          </DSPrimaryButton>
        </DSBox>

        <ReviewFormModal
          open={modalOpen}
          handleClose={handleModalClose}
          handleSubmitReview={handleSubmitReview}
          loading={isRejectPending || isApprovePending}
          state={reviewState}
          warningMessage={
            klydo?.strikesUrls && klydo.strikesUrls.length > 0
              ? "This klydo has strike audio. Please listen and verify the strike sounds correct before approving."
              : undefined
          }
        />

        <SendKlydoModal
          open={sendKlydoOpen}
          handleClose={() => setSendKlydoOpen(false)}
          klydos={[klydo]}
          onlyDev
        />
      </DSBox>
    </DSModal>
  );
};

export default KlydoReviewModal;
